/** Standing-user roles (ADR-004, docs/ROLES.md). Tournament-director codes are F-08. */
export const USER_ROLES = ['admin', 'equipment_admin', 'staff', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  equipment_admin: 'Equipment admin',
  staff: 'Staff',
  viewer: 'Viewer',
};

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as readonly string[]).includes(value);
}

/** Admin and equipment admin read the audit log (F-09 AC 3). Staff and viewers do not. */
export function roleCanViewAudit(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

/** Admin and equipment admin read and write app settings (F-09). */
export function roleCanManageSettings(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

/** Sliding session window (ADR-003). Trusted-device cookies use the same value. */
export const SESSION_TTL_SECONDS = 14 * 24 * 60 * 60;

/** Default grace after last member event end 23:59 America/Chicago (ADR-015, F-08). */
export const DEFAULT_TD_CODE_GRACE_HOURS = 24;

export const SESSION_COOKIE = 'ih_session';
export const DEVICE_COOKIE = 'ih_device';
export const OAUTH_STATE_COOKIE = 'ih_oauth';

/** What the client is allowed to know about the signed-in person. No tokens. */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  active: boolean;
}

/** Tournament-director session (F-08). Not a standing UserRole. */
export interface SessionCode {
  id: string;
  displayName: string;
  loadoutId: string;
  hint: string;
  expiresAt: string;
}

/** Admin and equipment admin mint and revoke event codes (US-03). */
export function roleCanManageEventCodes(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin';
}

/** F-18 / ADR-021: Admin, Equipment admin, and Staff may hold a machine token. */
export function roleCanMintMachineTokens(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin' || role === 'staff';
}

/** F-17 / ADR-020: only Admin sends a Zoho estimate or converts it to an invoice. */
export function roleCanSendZoho(role: UserRole): boolean {
  return role === 'admin';
}

export interface PublicApiToken {
  id: string;
  userId: string;
  tokenPrefix: string;
  label: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface ApiTokenListResponse {
  tokens: PublicApiToken[];
}

export interface ApiTokenCreateResponse {
  token: PublicApiToken;
  /** Raw Bearer secret. Shown once at mint; never stored or listed. */
  raw: string;
}

export interface ApiTokenResponse {
  token: PublicApiToken;
}

export interface PublicEventAccessCode {
  id: string;
  loadoutId: string;
  displayName: string;
  hint: string;
  expiresAt: string;
  createdAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
}

export interface EventAccessCodeListResponse {
  codes: PublicEventAccessCode[];
}

export interface EventAccessCodeCreateResponse {
  code: PublicEventAccessCode;
  /** Raw value. Shown once at creation; never stored or listed. */
  raw: string;
}

export interface EventAccessCodeResponse {
  code: PublicEventAccessCode;
}

export interface RedeemCodeRequest {
  code: string;
}

export interface PublicUser extends SessionUser {
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeResponse {
  user: SessionUser | null;
  code: SessionCode | null;
}

export interface UserListResponse {
  users: PublicUser[];
}

export interface UserResponse {
  user: PublicUser;
}

export interface LogoutResponse {
  ok: true;
}
